import { useCallback, useEffect, useState } from "react";
import { api, extractAxiosError } from "@/lib/api";
import {
    ANNOUNCEMENT_AUDIENCES,
    ANNOUNCEMENT_SEVERITIES,
    announcementStatusClass,
    announcementStatusLabel,
    emptyAnnouncement,
    fromDatetimeLocalValue,
    toDatetimeLocalValue,
} from "@/lib/siteConfigStorage";
import { useSiteConfig } from "@/contexts/SiteConfigContext";
import { Loader2, Megaphone, Plus, Save, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

function Toggle({ checked, onChange, label, description, testId }) {
    return (
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-white/20 bg-transparent accent-[#5d00ff]"
                data-testid={testId}
            />
            <span className="min-w-0">
                <span className="block text-sm font-medium text-white/90">{label}</span>
                {description ? <span className="mt-1 block text-xs leading-relaxed text-white/45">{description}</span> : null}
            </span>
        </label>
    );
}

function AnnouncementEditor({ value, onChange, onRemove, index }) {
    const update = (patch) => onChange({ ...value, ...patch });

    return (
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-4" data-testid={`admin-announcement-${index}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-white/85">Announcement {index + 1}</p>
                    {value.status ? (
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${announcementStatusClass(value.status)}`}>
                            {announcementStatusLabel(value.status)}
                        </span>
                    ) : null}
                </div>
                <button
                    type="button"
                    onClick={onRemove}
                    className="btn-ghost inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-rose-300 hover:text-rose-200"
                    data-testid={`admin-announcement-remove-${index}`}
                >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
            </div>

            <Toggle
                checked={Boolean(value.enabled)}
                onChange={(enabled) => update({ enabled })}
                label="Enabled"
                description="Disabled announcements are saved but never shown."
                testId={`admin-announcement-enabled-${index}`}
            />

            <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                    <label className="label-eyebrow">Title</label>
                    <input
                        value={value.title}
                        onChange={(e) => update({ title: e.target.value })}
                        maxLength={120}
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        placeholder="Scheduled maintenance tonight"
                        data-testid={`admin-announcement-title-${index}`}
                    />
                </div>
                <div className="sm:col-span-2">
                    <label className="label-eyebrow">Message</label>
                    <textarea
                        value={value.message}
                        onChange={(e) => update({ message: e.target.value })}
                        maxLength={500}
                        rows={3}
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        placeholder="We expect 30 minutes of downtime starting at 2:00 AM UTC."
                        data-testid={`admin-announcement-message-${index}`}
                    />
                </div>
                <div>
                    <label className="label-eyebrow">Severity</label>
                    <select
                        value={value.severity}
                        onChange={(e) => update({ severity: e.target.value })}
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        data-testid={`admin-announcement-severity-${index}`}
                    >
                        {ANNOUNCEMENT_SEVERITIES.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="label-eyebrow">Audience</label>
                    <select
                        value={value.audience}
                        onChange={(e) => update({ audience: e.target.value })}
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        data-testid={`admin-announcement-audience-${index}`}
                    >
                        {ANNOUNCEMENT_AUDIENCES.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="label-eyebrow">Starts (optional)</label>
                    <input
                        type="datetime-local"
                        value={toDatetimeLocalValue(value.starts_at)}
                        onChange={(e) => update({ starts_at: fromDatetimeLocalValue(e.target.value) })}
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        data-testid={`admin-announcement-starts-${index}`}
                    />
                </div>
                <div>
                    <label className="label-eyebrow">Ends (optional)</label>
                    <input
                        type="datetime-local"
                        value={toDatetimeLocalValue(value.ends_at)}
                        onChange={(e) => update({ ends_at: fromDatetimeLocalValue(e.target.value) })}
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        data-testid={`admin-announcement-ends-${index}`}
                    />
                </div>
                <div>
                    <label className="label-eyebrow">Link URL (optional)</label>
                    <input
                        value={value.link_url || ""}
                        onChange={(e) => update({ link_url: e.target.value })}
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        placeholder="https://status.fairshare.app"
                        data-testid={`admin-announcement-link-${index}`}
                    />
                </div>
                <div>
                    <label className="label-eyebrow">Link label (optional)</label>
                    <input
                        value={value.link_label || ""}
                        onChange={(e) => update({ link_label: e.target.value })}
                        className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm"
                        placeholder="View status page"
                        data-testid={`admin-announcement-link-label-${index}`}
                    />
                </div>
            </div>

            <Toggle
                checked={Boolean(value.dismissible)}
                onChange={(dismissible) => update({ dismissible })}
                label="Allow users to dismiss"
                description="Dismissals are stored locally in each browser."
                testId={`admin-announcement-dismissible-${index}`}
            />
        </div>
    );
}

function normalizeForSave(announcements) {
    return announcements.map((item) => ({
        id: item.id || null,
        enabled: Boolean(item.enabled),
        title: (item.title || "").trim(),
        message: (item.message || "").trim(),
        severity: item.severity || "info",
        link_url: (item.link_url || "").trim() || null,
        link_label: (item.link_label || "").trim() || null,
        starts_at: item.starts_at || null,
        ends_at: item.ends_at || null,
        dismissible: Boolean(item.dismissible),
        audience: item.audience || "all",
    }));
}

export function AdminSitePage() {
    const { refreshSiteConfig } = useSiteConfig();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [betaModeEnabled, setBetaModeEnabled] = useState(false);
    const [announcements, setAnnouncements] = useState([]);
    const [updatedAt, setUpdatedAt] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/site-settings");
            setBetaModeEnabled(Boolean(data.beta_mode_enabled));
            setAnnouncements((data.announcements || []).map((item) => ({
                ...item,
                link_url: item.link_url || "",
                link_label: item.link_label || "",
            })));
            setUpdatedAt(data.updated_at || null);
        } catch (e) {
            toast.error(extractAxiosError(e, "Could not load site settings"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        const cleaned = normalizeForSave(announcements);
        for (const item of cleaned) {
            if (!item.title || !item.message) {
                toast.error("Each announcement needs a title and message.");
                return;
            }
            if (item.starts_at && item.ends_at && new Date(item.ends_at) <= new Date(item.starts_at)) {
                toast.error("Announcement end time must be after the start time.");
                return;
            }
        }

        setSaving(true);
        try {
            const { data } = await api.patch("/admin/site-settings", {
                beta_mode_enabled: betaModeEnabled,
                announcements: cleaned,
            });
            setBetaModeEnabled(Boolean(data.beta_mode_enabled));
            setAnnouncements((data.announcements || []).map((item) => ({
                ...item,
                link_url: item.link_url || "",
                link_label: item.link_label || "",
            })));
            setUpdatedAt(data.updated_at || null);
            await refreshSiteConfig();
            toast.success("Site settings saved");
        } catch (e) {
            toast.error(extractAxiosError(e, "Could not save site settings"));
        } finally {
            setSaving(false);
        }
    };

    const addAnnouncement = () => {
        setAnnouncements((prev) => [...prev, emptyAnnouncement()]);
    };

    const updateAnnouncement = (index, next) => {
        setAnnouncements((prev) => prev.map((item, i) => (i === index ? next : item)));
    };

    const removeAnnouncement = (index) => {
        setAnnouncements((prev) => prev.filter((_, i) => i !== index));
    };

    if (loading) {
        return (
            <div className="grid min-h-[16rem] place-items-center text-sm text-white/45" data-testid="admin-site-loading">
                <Loader2 className="h-5 w-5 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="admin-site-page">
            <div className="hidden lg:block">
                <h2 className="font-heading text-xl tracking-tight">Site</h2>
                <p className="mt-1 max-w-2xl text-sm text-white/50">
                    Control platform-wide announcements shown at the top of the app, and toggle beta labelling across the product UI.
                </p>
            </div>

            <section className="space-y-4">
                <div className="flex items-center gap-2">
                    <Sparkles className="admin-accent-icon h-4 w-4" />
                    <h3 className="text-sm font-medium text-white/85">Beta mode</h3>
                </div>
                <Toggle
                    checked={betaModeEnabled}
                    onChange={setBetaModeEnabled}
                    label="Show FairShare is in BETA across the UI"
                    description="Adds a BETA badge in the header and a notice on the dashboard. Useful while the product is in early access."
                    testId="admin-beta-mode-toggle"
                />
            </section>

            <section className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Megaphone className="admin-accent-icon h-4 w-4" />
                        <div>
                            <h3 className="text-sm font-medium text-white/85">Announcements</h3>
                            <p className="text-xs text-white/45">Schedule banners with optional start/end times and audience targeting.</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={addAnnouncement}
                        className="btn-ghost inline-flex items-center gap-1.5 rounded-md border border-white/[0.08] px-3 py-2 text-xs"
                        data-testid="admin-announcement-add"
                    >
                        <Plus className="h-3.5 w-3.5" /> Add announcement
                    </button>
                </div>

                {announcements.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-white/[0.08] bg-white/[0.02] px-4 py-10 text-center text-sm text-white/45">
                        No announcements yet. Add one to show a banner at the top of the site.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {announcements.map((item, index) => (
                            <AnnouncementEditor
                                key={item.id || `new-${index}`}
                                value={item}
                                index={index}
                                onChange={(next) => updateAnnouncement(index, next)}
                                onRemove={() => removeAnnouncement(index)}
                            />
                        ))}
                    </div>
                )}
            </section>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] pt-4">
                <p className="text-xs text-white/40">
                    {updatedAt ? `Last saved ${new Date(updatedAt).toLocaleString()}` : "Not saved yet"}
                </p>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="btn-primary inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm disabled:opacity-60"
                    data-testid="admin-site-save"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save changes
                </button>
            </div>
        </div>
    );
}
